import React from "react";
import { Send, Sparkles, RadioTower, Eye, Crosshair, Layers, ScanSearch, Trash2, Activity } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import useChatSession, { useAutoScroll } from "../components/chat/useChatSession";
import useMessageInput from "../components/chat/useMessageInput";
import MessageList from "../components/chat/MessageList";

/**
 * House of Intelligence — full-page AI cockpit.
 * Tema chiaro: sfondo bianco con forte contrasto blu scuro (#0D1B3D).
 */
const NAVY = "#0D1B3D";

export default function HouseOfIntelligence() {
  const { t, lang } = useLanguage();
  const locale = lang === "en" ? "en-US" : "it-IT";

  const session = useChatSession({ enabled: true, loadTrigger: 1 });
  const input = useMessageInput({
    onSend: session.send,
    disabled: session.sending,
    focusKey: 1,
  });
  const scrollRef = useAutoScroll([session.messages, session.sending]);

  const suggestions = [
    {
      icon: ScanSearch,
      label: t("hoi.suggest.companies"),
      query: "Quali aziende del settore tech hanno aperto sedi in Italia negli ultimi 12 mesi?",
    },
    {
      icon: Crosshair,
      label: t("hoi.suggest.competitors"),
      query: "Genera un'analisi comparativa dei principali competitor nel mercato manifatturiero italiano.",
    },
    {
      icon: Layers,
      label: t("hoi.suggest.patents"),
      query: "Riassumi i brevetti più recenti nel settore industria 4.0 con punteggio sopra 80.",
    },
    {
      icon: Eye,
      label: t("hoi.suggest.warroom"),
      query: "Quali sono i clienti attivi e in quali mercati operano?",
    },
  ];

  const hasMessages = session.messages.length > 0;

  return (
    <div
      data-testid="hoi-page"
      className="flex h-[calc(100vh-64px)] bg-white"
      style={{ fontFamily: "Chivo, sans-serif", color: NAVY }}
    >
      {/* ───── Left rail ───── */}
      <aside className="hidden lg:flex flex-col w-72 border-r border-slate-200 bg-slate-50">
        <div className="px-5 py-5 border-b border-slate-200">
          <div className="flex items-center gap-2 mb-1">
            <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
            <span className="text-[10px] tracking-[0.25em] uppercase text-emerald-600 font-semibold">
              {t("hoi.link_online")}
            </span>
          </div>
          <div className="text-lg font-black tracking-tight" style={{ color: NAVY }}>
            AI AGENT JAMES
          </div>
          <p className="text-[10px] text-slate-500 mt-1 tracking-widest uppercase">
            {t("hoi.subtitle")}
          </p>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          <section>
            <p className="text-[10px] text-slate-500 tracking-[0.2em] uppercase mb-2 font-semibold">
              {t("hoi.briefing")}
            </p>
            <ul className="space-y-2 text-xs text-slate-700">
              <Briefing icon={ScanSearch} text={t("hoi.brief_1")} />
              <Briefing icon={Layers} text={t("hoi.brief_2")} />
              <Briefing icon={Eye} text={t("hoi.brief_3")} />
              <Briefing icon={Crosshair} text={t("hoi.brief_4")} />
            </ul>
          </section>

          <section>
            <p className="text-[10px] text-slate-500 tracking-[0.2em] uppercase mb-2 font-semibold">
              {t("hoi.session")}
            </p>
            <div className="px-3 py-3 bg-white border border-slate-200 rounded-md" style={{ borderLeft: `3px solid ${NAVY}` }}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] tracking-widest uppercase font-bold" style={{ color: NAVY }}>
                  {t("hoi.mission")} 001
                </span>
                <span className="text-[9px] text-slate-500 font-mono">
                  {session.messages.length} {t("hoi.exchanges")}
                </span>
              </div>
              <p className="text-[10px] text-slate-500 leading-relaxed">
                {hasMessages
                  ? t("hoi.session_active")
                  : t("hoi.session_idle")}
              </p>
              {hasMessages && (
                <button
                  data-testid="hoi-clear-session"
                  onClick={session.clear}
                  className="mt-3 w-full flex items-center justify-center gap-1.5 px-2 py-1.5 border border-red-300 text-red-600 text-[10px] uppercase tracking-widest hover:bg-red-50 transition-colors rounded"
                >
                  <Trash2 className="w-3 h-3" />
                  {t("hoi.purge_session")}
                </button>
              )}
            </div>
          </section>
        </div>

        <div className="px-5 py-4 border-t border-slate-200 text-[9px] text-slate-500 tracking-widest uppercase">
          <div className="flex items-center gap-2 mb-1">
            <Activity className="w-3 h-3" style={{ color: NAVY }} />
            <span>{t("hoi.uplink")}</span>
          </div>
          <div className="font-mono font-bold" style={{ color: NAVY }}>AI AGENT JAMES · v1.4.0</div>
          <div className="text-slate-500 mt-0.5">{t("hoi.classified")}</div>
        </div>
      </aside>

      {/* ───── Main column ───── */}
      <main className="flex-1 flex flex-col min-w-0 bg-white">
        {/* Header */}
        <header className="flex items-center gap-3 px-6 py-4 border-b border-slate-200 bg-white">
          <div
            className="w-11 h-11 flex items-center justify-center rounded-md relative"
            style={{ backgroundColor: NAVY }}
          >
            <RadioTower className="w-5 h-5" style={{ color: "#FFFFFF" }} strokeWidth={2.25} />
            <span className="absolute -top-1 -right-1 w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="text-base font-bold tracking-wide" style={{ color: NAVY }}>AI AGENT JAMES</h1>
              <span
                className="text-[9px] tracking-widest uppercase px-1.5 py-0.5 font-bold rounded"
                style={{ color: "#fff", backgroundColor: "#059669" }}
              >
                {t("hoi.encrypted")}
              </span>
            </div>
            <p className="text-[10px] text-slate-500 tracking-widest uppercase mt-0.5">
              {t("hoi.header_caption")}
            </p>
          </div>
          <div className="hidden sm:flex items-center gap-3 text-[10px] text-slate-500 tracking-widest uppercase font-mono">
            <span><span style={{ color: NAVY }}>●</span> NET</span>
            <span><span style={{ color: NAVY }}>●</span> DB</span>
            <span><span style={{ color: NAVY }}>●</span> AI</span>
          </div>
        </header>

        {/* Body */}
        {!hasMessages && !session.loadingHistory ? (
          <div className="flex-1 overflow-y-auto px-6 py-6 sm:py-8 lg:py-10 bg-slate-50">
            <div className="max-w-3xl mx-auto">
              <div className="flex flex-col items-center text-center mb-10">
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center mb-4 relative"
                  style={{ backgroundColor: NAVY }}
                >
                  <Sparkles className="w-8 h-8" style={{ color: "#FFFFFF" }} strokeWidth={2.25} />
                  <span
                    className="absolute inset-0 rounded-full border-2 animate-ping"
                    style={{ borderColor: `${NAVY}40` }}
                  />
                </div>
                <h2 className="text-3xl font-black tracking-tight mb-2" style={{ color: NAVY }}>
                  {t("hoi.welcome_title")}
                </h2>
                <p className="text-sm text-slate-600 max-w-xl">
                  {t("hoi.welcome_subtitle")}
                </p>
              </div>

              <p className="text-[10px] text-slate-500 tracking-[0.2em] uppercase mb-3 font-semibold">
                {t("hoi.suggestions_title")}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {suggestions.map((s) => (
                  <button
                    key={s.label}
                    data-testid={`hoi-suggestion-${s.label}`}
                    onClick={() => session.send(s.query)}
                    className="group flex items-start gap-3 px-4 py-3 bg-white border border-slate-200 hover:shadow-md transition-all text-left rounded-md"
                    style={{ borderLeft: `3px solid ${NAVY}` }}
                  >
                    <span
                      className="w-10 h-10 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform rounded"
                      style={{ backgroundColor: NAVY }}
                    >
                      <s.icon className="w-5 h-5" style={{ color: "#FFFFFF" }} strokeWidth={2.25} />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-bold mb-1" style={{ color: NAVY }}>{s.label}</div>
                      <div className="text-[11px] text-slate-600 leading-snug line-clamp-2">
                        {s.query}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <MessageList
            ref={scrollRef}
            messages={session.messages}
            sending={session.sending}
            loadingHistory={session.loadingHistory}
            locale={locale}
            onPickSuggestion={session.send}
          />
        )}

        {/* Input bar */}
        <div className="border-t border-slate-200 bg-white px-6 py-4">
          <form onSubmit={input.submit} className="max-w-3xl mx-auto">
            <div
              className="flex items-center gap-3 bg-slate-50 border border-slate-200 focus-within:border-[color:var(--focus)] transition-colors px-4 py-3 rounded-md"
              style={{ ["--focus"]: NAVY }}
            >
              <RadioTower className="w-4 h-4 flex-shrink-0" style={{ color: NAVY }} />
              <input
                ref={input.inputRef}
                data-testid="hoi-input"
                type="text"
                value={input.value}
                onChange={(e) => input.setValue(e.target.value)}
                disabled={session.sending}
                placeholder={t("hoi.placeholder")}
                className="flex-1 bg-transparent text-sm placeholder:text-slate-400 focus:outline-none disabled:opacity-60"
                style={{ color: NAVY }}
              />
              <button
                data-testid="hoi-send"
                type="submit"
                disabled={session.sending || !input.value.trim()}
                className="flex items-center justify-center w-10 h-10 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity rounded"
                style={{ backgroundColor: NAVY }}
              >
                <Send className="w-5 h-5" style={{ color: "#FFFFFF" }} strokeWidth={2.25} />
              </button>
            </div>
            <div className="flex items-center justify-between mt-2 px-1">
              <span className="text-[9px] text-slate-500 tracking-widest uppercase">
                {t("hoi.input_hint")}
              </span>
              <span className="text-[9px] tracking-widest uppercase font-mono" style={{ color: NAVY, opacity: 0.7 }}>
                {t("hoi.disclaimer")}
              </span>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}

function Briefing({ icon: Icon, text }) {
  return (
    <li className="flex items-start gap-2">
      <Icon className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: NAVY }} />
      <span className="leading-relaxed">{text}</span>
    </li>
  );
}
